import { useMemo } from "react";
import PropTypes from "prop-types";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";

const LightTooltip = styled(({ className, ...props }) => (
    <Tooltip
        {...props}
        classes={{ popper: className }}
    />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.white,
        color: "rgba(0, 0, 0, 0.87)",
        fontSize: 11,
    },
}));

function HeaderSampleSize({ productData = {}, productColumnVisibility = {}, pageName = null, sampleInfo = {}, draggableColumns = [], isProductHeader = true }) {
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);

    const isHairCare = process.env?.REACT_APP_HAIR_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isSkinCare = process.env?.REACT_APP_SKIN_CARE?.toLowerCase() === productCategoryData?.label?.toLowerCase();
    const isBeautyAndWellBeing = isHairCare || isSkinCare;

    const { count, color, tooltipMessage } = useMemo(() => {
        let _sampleSize = 0;

        if (isProductHeader) {
            _sampleSize = pageName === "correlation" ? sampleInfo[productData.title] : productData.sampleSize;
        } else {
            draggableColumns.forEach((datum) => {
                const key = `checked--${datum.title.replace(/\s+/g, "_")}__${datum.coreDataId}`;
                if (!productColumnVisibility[key]) {
                    _sampleSize += datum.sampleSize;
                }
            });
        }

        // Condition for Beauty & Wellbeing (Hair Care and Skin Care) with n < 90
        if (isBeautyAndWellBeing && _sampleSize < 90) {
            return {
                count: _sampleSize,
                color: "#FF0000", // Red color for sample size
                tooltipMessage: "Sample Size is below 90 (Qualitative Data)", // Updated message for Beauty & Wellbeing
            };
        }
        // Conditions for other categories
        else if (_sampleSize <= 30) {
            return {
                count: _sampleSize,
                color: "#FF0000",
                tooltipMessage: "Sample size is below 30",
            };
        } else if (_sampleSize <= 75) {
            return {
                count: _sampleSize,
                color: "#ffa500",
                tooltipMessage: "The sample size falls between 31 and 75",
            };
        }
        return { count: _sampleSize, color: "#000000", tooltipMessage: null };
    }, [sampleInfo, pageName, productData, draggableColumns, productColumnVisibility, isProductHeader, isBeautyAndWellBeing]); // Added isBeautyAndWellBeing to dependency array

    // Determine if icon should be shown based on category
    const shouldShowIcon = isBeautyAndWellBeing ? count < 90 : count <= 75;

    return (
        <span
            style={{
                fontSize: "12px",
                textAlign: "center",
                fontWeight: "bold",
            }}
        >
            <span
                style={{
                    color: color,
                }}
            >
                (n={count})
            </span>
            <sup>
                {shouldShowIcon && tooltipMessage && (
                    <LightTooltip
                        title={tooltipMessage}
                        placement="right"
                    >
                        <InfoOutlinedIcon
                            style={{
                                fontSize: "11px",
                                color: color,
                                cursor: "pointer",
                            }}
                        />
                    </LightTooltip>
                )}
            </sup>
        </span>
    );
}

HeaderSampleSize.propTypes = {
    productData: PropTypes.object,
    productColumnVisibility: PropTypes.object,
    pageName: PropTypes.string,
    sampleInfo: PropTypes.object,
    draggableColumns: PropTypes.array,
    isProductHeader: PropTypes.bool,
};

export default HeaderSampleSize;
